/* eslint-disable @typescript-eslint/no-explicit-any */

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	TProduction,
	TSortColumn,
} from '@global-types/index';
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';

export interface IProductionStartMintSlice {
	productionStartMintsPage: number;
	productionStartMintsTotal: number;
	productionStartMintsPageSize: number;
	productionStartMintsSelectedRecords: TProduction[];
	productionStartMintsQueryParams: any;
	productionStartMintsSortStatus: any;
	productionStartMintsSortParams: string;
	productionStartMintsSelectedRow: Partial<TProduction>;
	productionStartMintsProducer: string;
	productionStartMintsProject: string;
	productionStartMintsActiveStep: number;
	productionStartMintsFilterDate: string;
	productionStartMintsTotalPages: number;
	productionStartMintsOpenConfirmationModalDelete: any;
	productionStartMintsOpenConfirmationModalMint: any;
	productionStartMintsOpenConfirmationModalVerify: any;
	productionStartMintsSelectedRecordsStep3: Partial<TProduction>;

	setProductionStartMintsSelectedRecordsStep3: (
		productionStartMintsSelectedRecordsStep3: TProduction[]
	) => void;
	setProductionStartMintsFilterDate: (
		productionStartMintsFilterDate: string
	) => void;
	setProductionsStartMintsProject: (
		_productionStartMintsProject: string
	) => void;
	setProductionsStartMintsActiveStep: (
		_productionStartMintsActiveStep: number
	) => void;
	setProductionsStartMintsProducer: (
		_productionStartMintsProducer: string
	) => void;
	setProductionsStartMintsPage: (
		_productionStartMintsPage: number
	) => void;
	setProductionsStartMintsSelectedRecords: (
		_productionStartMintsPage: any
	) => void;
	setProductionsStartMintsOpenConfirmationModalDelete: (
		_productionStartMintsOpenConfirmationModalDelete: any
	) => void;
	setProductionsStartMintsQueryParams: (
		_productionStartMintsQueryParams: any
	) => void;
	setProductionsStartMintsSortStatus: (
		_productionStartMintsSortStatus: TSortColumn
	) => void;
	setProductionsStartMintsSortParams: (
		_productionStartMintsSortParams: string
	) => void;
	setProductionsStartMintsSelectedRow: (
		_productionStartMint: TProduction
	) => void;
	setProductionsStartMintsPageSize: (
		_pageSize: any
	) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	productionStartMintsPage: 1,
	productionStartMintsTotal: 0,
	productionStartMintsPageSize: 10,
	productionStartMintsSelectedRecords: [],
	productionStartMintsQueryParams: {
		verified: true,
	},
	productionStartMintsSortStatus: {},
	productionStartMintsSortParams: 'startDate',
	productionStartMintsProducer: '',
	productionStartMintsProject: '',
	productionStartMintsSelectedRow: {},
	productionStartMintsTotalPages: 0,
	productionStartMints: [],
	productionStartMintsActiveStep: 0,
	productionStartMintsFilterDate: '',
	productionStartMintsOpenConfirmationModalDelete: {},
	productionStartMintsOpenConfirmationModalMint: {},
	productionStartMintsOpenConfirmationModalVerify: {},
	productionStartMintsSelectedRecordsStep3: [],
};

export const createProductionStartMintSlice: StateCreator<
	TStore,
	[],
	[],
	IProductionStartMintSlice
> = (set) => ({
	...initialState,
	setProductionStartMintsSelectedRecordsStep3: (
		_productionStartMintsSelectedRecordsStep3: Partial<TProduction>
	) =>
		set({
			productionStartMintsSelectedRecordsStep3:
				_productionStartMintsSelectedRecordsStep3,
		}),

	setProductionStartMintsFilterDate: (date: string) =>
		set({ productionStartMintsFilterDate: date }),
	setProductionsStartMintsProject: (
		_productionStartMintsProject: string
	) =>
		set({
			productionStartMintsProject:
				_productionStartMintsProject,
		}),
	setProductionsStartMintsActiveStep: (_step: number) =>
		set({ productionStartMintsActiveStep: _step }),

	setProductionsStartMintsProducer: (
		_productionStartMintsProducer: string
	) =>
		set({
			productionStartMintsProducer:
				_productionStartMintsProducer,
			productionStartMintsQueryParams: {
				...initialState.productionStartMintsQueryParams,
				producer: _productionStartMintsProducer,
				status: 'AVAILABLE',
			},
		}),
	setProductionsStartMintsPage: (_page: number) =>
		set({ productionStartMintsPage: _page }),
	setProductionsStartMintsSelectedRecords: (
		_productionStartMintSelectedRecords: TProduction[]
	) =>
		set({
			productionStartMintsSelectedRecords:
				_productionStartMintSelectedRecords,
		}),
	setProductionsStartMintsOpenConfirmationModalDelete:
		(_productionStartMintOpenConfirmationModalDelete: {
			open: boolean;
			params: any;
		}) =>
			set({
				productionStartMintsOpenConfirmationModalDelete:
					_productionStartMintOpenConfirmationModalDelete,
			}),
	setProductionsStartMintsQueryParams: (
		_queryParams: number
	) =>
		set({
			productionStartMintsQueryParams: _queryParams,
		}),
	setProductionsStartMintsSortStatus: (
		_sortStatus: TSortColumn
	) =>
		set({
			productionStartMintsSortStatus: _sortStatus,
		}),

	setProductionsStartMintsSelectedRow: (
		_productionStartMint: TProduction
	) =>
		set({
			productionStartMintsSelectedRow:
				_productionStartMint,
		}),
	setProductionsStartMintsSortParams: (
		_sortStatus: string
	) =>
		set({
			productionStartMintsSortParams: _sortStatus,
		}),

	setProductionsStartMintsTotal: (_total: number) =>
		set({ productionStartMintsTotal: _total }),

	setProductionsStartMintsOpenConfirmationModalMint:
		(_productionStartMintOpenConfirmationModalMint: {
			open: boolean;
			params: any;
		}) =>
			set({
				productionStartMintsOpenConfirmationModalMint:
					_productionStartMintOpenConfirmationModalMint,
			}),
	setProductionsStartMintsOpenConfirmationModalVerify:
		(_productionStartMintOpenConfirmationModalVerify: {
			open: boolean;
			params: any;
		}) =>
			set({
				productionStartMintsOpenConfirmationModalVerify:
					_productionStartMintOpenConfirmationModalVerify,
			}),

	setProductionsStartMintsPageSize: (_pageSize: number) =>
		set({
			productionStartMintsPageSize: _pageSize,
		}),

	setProductionsStartMintsTotalPages: (
		_totalPages: number
	) =>
		set({
			productionStartMintsTotalPages: _totalPages,
		}),
});
